/**
 * A skill in the library: a tile coloured by its name, its name and trigger, and
 * the scope it comes from.
 *
 * Skills are not a personal collection on this surface — the org's library is
 * what the agent draws on — so the row states where a skill comes from rather
 * than offering an add/remove toggle that has nothing behind it.
 *
 * @module components/connections/SkillRow
 */
import {
  ChevronRightIcon,
  FileTextIcon,
  PenLineIcon,
  PresentationIcon,
  SparklesIcon,
  Table2Icon,
  type LucideIcon,
} from "lucide-react";

import { cn } from "../../lib/utils";
import type { SkillView } from "../../state/connections";
import { getSkillSource, SKILL_SOURCE_LABEL } from "./skillContent";
import { skillColour } from "./skillColour";
import { Button } from "../ui/button";

const CATEGORY_TILES: ReadonlyArray<{
  readonly match: RegExp;
  readonly icon: LucideIcon;
}> = [
  {
    match: /document edit/i,
    icon: PenLineIcon,
  },
  {
    match: /document/i,
    icon: FileTextIcon,
  },
  {
    match: /spreadsheet/i,
    icon: Table2Icon,
  },
  {
    match: /presentation/i,
    icon: PresentationIcon,
  },
];

const DEFAULT_TILE = {
  icon: SparklesIcon,
};

export function SkillTile({
  name,
  category,
  className,
}: {
  readonly name: string;
  readonly category: string | null;
  readonly className?: string;
}) {
  const tile = CATEGORY_TILES.find((entry) => entry.match.test(category ?? "")) ?? DEFAULT_TILE;
  const Icon = tile.icon;
  return (
    <span
      aria-hidden
      data-slot="skill-tile"
      className={cn(
        "flex size-10 shrink-0 items-center justify-center rounded-xl",
        skillColour(name),
        className,
      )}
    >
      <Icon className="size-5 text-current" />
    </span>
  );
}

export type OpenSkill = (skill: SkillView, trigger: HTMLElement) => void;

export function SkillRow({
  skill,
  onOpen,
}: {
  readonly skill: SkillView;
  readonly onOpen: OpenSkill;
}) {
  return (
    <article className="min-w-0 py-1" aria-label={skill.name}>
      <Button
        variant="ghost"
        aria-label={`View ${skill.name}`}
        aria-haspopup="dialog"
        onClick={(event) => onOpen(skill, event.currentTarget)}
        className="group h-auto w-full items-start justify-start gap-3 whitespace-normal rounded-xl px-2 py-3 text-left transition-colors active:scale-100 sm:h-auto"
      >
        <SkillTile
          name={skill.name}
          category={skill.category}
          className="mt-0.5 size-9 rounded-lg"
        />
        <span className="min-w-0 flex-1">
          <span className="block break-words text-sm font-medium leading-5 text-foreground">
            {skill.name}
          </span>
          <span className="mt-1 block text-xs font-normal text-muted-foreground">
            {SKILL_SOURCE_LABEL[getSkillSource(skill)]}
            {skill.category ? ` · ${skill.category}` : ""}
          </span>
          <span className="mt-2 block break-words text-sm font-normal leading-6 text-muted-foreground">
            {skill.description}
          </span>
        </span>
        <ChevronRightIcon
          aria-hidden
          className="mt-2.5 size-4 text-muted-foreground/50 transition-colors group-hover:text-foreground group-focus-visible:text-foreground"
        />
      </Button>
    </article>
  );
}
